import { Dispatch, SetStateAction } from 'react';
import store from 'store';
import axios from 'axios';

import { MethodsTypes, IRequestConfig } from 'models/Axios';
import { IDownloadTypes, IOrderItem, ICompanies, InvoiceTypes, IMinimumOrderForDownload } from 'models/Order';
import { DownloadInLotRequestProps } from 'models/Download';
import { CTeInvoiceState, CTeState } from 'models/CTEs';
import { ITickets } from 'models/Ticket';

import toast from 'components/Toast/dispatch';

import TicketServices from 'services/ticket.service';
import OrderSevice from 'services/order.service';
import Services from './index.service';

const _ticketService = new TicketServices();
const _orderService = new OrderSevice();

export default class DownloadService extends Services<any, any> {
  private endpoint = `${this.base_url}/express-download`;

  private async GetBody(downloadType: IDownloadTypes, order?: IMinimumOrderForDownload, orderItem?: IOrderItem) {
    const body = {
      isXml: false,
      invoiceType: InvoiceTypes.INVOICE,
      forceDownload: true,
      communicationChannel: "PORTAL",
      subsidiary: order?.subsidiary,
      enterpriseId: order?.enterpriseCode,
      invoiceNumber: order?.invoiceNumber
    };

    switch (downloadType) {
      case IDownloadTypes.BILLET:
        return {
          ...body,
          attachmentType: "BILLET",
          attachmentFilter: "ORDER",
          identifier: order?.protheusOrderSale,
        };

      case IDownloadTypes.INVOICE:
        return {
          ...body,
          attachmentType: "INVOICE",
          attachmentFilter: "ORDER",
          identifier: order?.protheusOrderSale,
        };

      case IDownloadTypes.RECORD:
        const orderIdRecord = await _orderService.getId(orderItem?.orderId);
        return {
          ...body,
          attachmentType: "DATASHEET",
          attachmentFilter: "ORDER_PRODUCT",
          identifier: orderIdRecord.data?.protheusOrderSale ?? orderIdRecord.data.id,
          subsidiary: orderIdRecord.data?.subsidiary,
          invoiceNumber: orderIdRecord.data?.invoiceNumber,
          product: orderItem?.code
        };

      case IDownloadTypes.REPORT:
        const orderIdReport = await _orderService.getId(orderItem?.orderId);
        return {
          ...body,
          attachmentType: "TECHNICAL_REPORTS",
          attachmentFilter: "ORDER_PRODUCT",
          identifier: orderIdReport.data?.protheusOrderSale ?? orderIdReport.data.id,
          subsidiary: orderIdReport.data?.subsidiary,
          product: orderItem?.code,
          lot: orderItem?.lot,
          invoiceNumber: orderIdReport?.data.invoiceNumber,
          enterpriseId: orderIdReport.data?.enterpriseCode
        };

      case IDownloadTypes.RECEIPT:
        return {
          ...body,
          attachmentType: "RECEIPT",
          attachmentFilter: "ORDER",
          identifier: order?.protheusOrderSale,
        };

      case IDownloadTypes.INVOICE_XML:
        return {
          ...body,
          isXml: true,
          attachmentType: "INVOICE_XML",
          attachmentFilter: "ORDER",
          identifier: order?.protheusOrderSale,
        };

      case IDownloadTypes.LETTERS:
        return {
          ...body,
          attachmentType: "FOUL_LETTER",
          attachmentFilter: "INVOICE",
          identifier: "string pedido ou nota ou produto",
        };

      case IDownloadTypes.DEVOLUTION_INVOICE:
        return {
          ...body,
          invoiceType: InvoiceTypes.INVOICE_DEVOLUTION,
          attachmentType: "DEVOLUTION_INVOICE",
          attachmentFilter: "ORDER",
          identifier: order?.protheusOrderSale,
        };

      case IDownloadTypes.DEVOLUTION_INVOICE_XML:
        return {
          ...body,
          invoiceType: InvoiceTypes.INVOICE_DEVOLUTION,
          isXml: true,
          attachmentType: "DEVOLUTION_INVOICE_XML",
          attachmentFilter: "ORDER",
          identifier: order?.protheusOrderSale,
        };

      case IDownloadTypes.INVOICE_SHIPPING:
        return {
          ...body,
          invoiceType: InvoiceTypes.INVOICE_SHIPPING,
          attachmentType: "INVOICE_SHIPPING",
          attachmentFilter: "ORDER",
          identifier: order?.protheusOrderSale,
        };

      case IDownloadTypes.INVOICE_SHIPPING_XML:
        return {
          ...body,
          isXml: true,
          invoiceType: InvoiceTypes.INVOICE_SHIPPING,
          attachmentType: "INVOICE_SHIPPING",
          attachmentFilter: "ORDER",
          identifier: order?.protheusOrderSale,
        };

      case IDownloadTypes.RECEIPT_SHIPPING:
        return {
          ...body,
          invoiceType: InvoiceTypes.INVOICE_SHIPPING,
          attachmentType: "RECEIPT_SHIPPING",
          attachmentFilter: "ORDER",
          identifier: order?.protheusOrderSale
        };

      default:
        toast.error('Desculpe, não foi possível realizar essa ação. Tente novamente mais tarde.');
        return null;
    }
  }

  private GetMessageError(downloadType: IDownloadTypes, result: any, userType?: string) {

    const typeDownloadInvoice = [
      IDownloadTypes.INVOICE,
    ];

    const typeDownloadInvoiceCTE = [
      IDownloadTypes.ELECTRONIC_BILL_LADING
    ];

    const typeDownloadInvoiceXML = [
      IDownloadTypes.INVOICE_XML,
      IDownloadTypes.ELECTRONIC_BILL_LADING_XML
    ];

    const typeDownloadBillet = [
      IDownloadTypes.BILLET,
      IDownloadTypes.INVOICE_BILL
    ];

    const company = result.data.ticket?.products?.[0]?.enterpriseCode;
    let title = '';
    const msg = 'Você gostaria de abrir um protocolo automático para enviarmos essa informação por e-mail?';
    if (typeDownloadBillet.includes(downloadType)) {
      if (result.data?.errorCode === 'CRM_MISSING_FILE' || result.data?.data?.length === 0) {
        if (userType === 'employee') {
          title = result.data?.message;
        } else {
          title = 'Boleto indisponível';
        }
      }
      title = `O boleto selecionado está indisponível`;
    } else if (typeDownloadInvoice.includes(downloadType)) {
      title = `A nota fiscal selecionada está indisponível`;
    } else if (typeDownloadInvoiceCTE.includes(downloadType)) {
      title = `O CTe selecionado está indisponível`;
    } else if (typeDownloadInvoiceXML.includes(downloadType)) {
      title = `O XML selecionado está indisponível`;
    } else if (downloadType === IDownloadTypes.RECEIPT) {
      title = `O canhoto selecionado está indisponível`;
    } else if (downloadType === IDownloadTypes.REPORT) {
      if (company === ICompanies.EXPRESSA) {
        title = `O laudo técnico selecionado não está disponível`;
      }
      title = `O laudo técnico selecionado está indisponível`;
    } else if (downloadType === IDownloadTypes.RECORD) {
      if (company === ICompanies.EXPRESSA) {
        title = `A ficha técnica selecionada não está disponível`;
      }
      title = `A ficha técnica selecionada está indisponível`;
    } else if (downloadType === IDownloadTypes.LETTERS) {
      title = `A carta de falta selecionada está indisponível`;
    } else {
      title = `O serviço selecionado está indisponível`;
    }

    toast.yesNoProtocol(title, msg,
      () => this.CreateTicket(result.data.ticket),
      'Sim',
      'Não'
    );
  }

  public async OpenFile(params: {}, downloadType: IDownloadTypes, userType?: string) {
    const config: IRequestConfig<any> = {
      url: this.endpoint,
      method: MethodsTypes.post,
      headers: {
        Authorization: `Bearer ${this.access_token()}`,
      },
      data: params,
    };

    return await axios(config)
      .then((result) => {
        //TODO : REFATORAR ESSES "DATA DATA DATA DATA DATA"
        if (result.data?.data?.data?.length === 0) {
          this.GetMessageError(downloadType, result.data, userType);
        } else {
          if (result.data?.data?.ok) {
            if (result.data.data.data?.uri) {
              DownloadService.CreateDownloadLink(result.data?.data?.data?.uri);
            } else if (result.data.data) {
              result.data.data.data.map((file: any) => {
                DownloadService.CreateDownloadWithBlank(file.uri);
              });
            } else {
              const myWindow = window.open('');
              myWindow && (myWindow.document.body.innerHTML = result.data);
            }
          } else if (result.data?.data?.ticket) {
            this.GetMessageError(downloadType, result.data, userType);
          } else {
            toast.error('Desculpe, erro inesperado!');
          }
        }
      })
      .catch(() => {
        toast.error('Desculpe, erro inesperado!');
      });
  }

  private async getFaultLetter(params: {}, downloadType: IDownloadTypes) {
    const { customer } = store.getState();
    const { user } = store.getState();
    const fixParam = { customerId: customer?.id ?? user?.selectedCustomerId };

    params = { ...params, ...fixParam };

    const config: IRequestConfig<any> = {
      url: `${this.base_url}/foulLetters/download`,
      method: MethodsTypes.get,
      headers: {
        Authorization: `Bearer ${this.access_token()}`,
      },
      params,
    };

    return await axios(config)
      .then((result) => {
        if (result.data?.ok) {
          if (result.data.data?.uri) {
            DownloadService.CreateDownloadLink(result.data.data?.uri);
          } else if (result.data.data) {
            result.data.data.map((file: any) => {
              DownloadService.CreateDownloadLink(file.uri);
            });
          } else {
            const myWindow = window.open('');
            myWindow && (myWindow.document.body.innerHTML = result.data);
          }
        } else if (result.data?.ticket) {
          this.GetMessageError(downloadType, result);
        } else {
          toast.error('Desculpe, erro inesperado!');
        }
      })
      .catch(() => {
        toast.error('Desculpe, erro inesperado!');
      });
  }

  public async OpenFileFromMissingLetter(missingLetterId: string) {
    if (missingLetterId == null)
      return;

    return await this.getFaultLetter({ id: missingLetterId, }, IDownloadTypes.LETTERS);
  }

  public async OpenFileFromOrder(downloadType: IDownloadTypes, order: IMinimumOrderForDownload, userType?: string) {
    const _params = await this.GetBody(downloadType, order);

    if (_params == null)
      return;

    return await this.OpenFile(_params, downloadType, userType);
  }

  public async OpenFileFromOrderItem(downloadType: IDownloadTypes, orderItem: IOrderItem) {
    const _params = await this.GetBody(downloadType, undefined, orderItem);

    if (_params == null)
      return;

    return await this.OpenFile(_params, downloadType);
  }

  public async OpenFileFromCTE(downloadType: IDownloadTypes, cte: CTeState) {
    const _params = {
      attachmentType: "ELECTRONIC_BILL_LADING",
      attachmentFilter: "Invoice",
      identifier: cte.id,
      isXml: downloadType === IDownloadTypes.ELECTRONIC_BILL_LADING_XML,
      communicationChannel: "Portal",
      useCache: true,
      invoiceType: InvoiceTypes.ELECTRONIC_BILL_LADING,
      forceDownload: true
    };

    return await this.OpenFile(_params, downloadType);
  }

  public async OpenFileFromCTeInvoice(downloadType: IDownloadTypes, cte: CTeInvoiceState) {
    const _params = {
      attachmentType: "INVOICE_BILL",
      attachmentFilter: "Invoice",
      identifier: cte?.id,
      isXml: false,
      communicationChannel: "Portal",
      useCache: true,
      invoiceType: InvoiceTypes.INVOICE_BILL,
      forceDownload: true
    };

    return await this.OpenFile(_params, downloadType);
  }

  private async CreateTicket(ticket: ITickets) {
    await _ticketService
      .create(ticket)
      .then((response) => {
        toast.success(
          `O protocolo ${response.data.data.protocolNumber} foi registrado com sucesso!`
        );
      })
      .catch(() => {
        toast.error('Ocorreu um erro durante a requisição');
      });
  }

  public async DownloadInLot(customerCareUserId: string | undefined, orderIds: string[], orderAttachmentType: number, downloadType: number) {
    const config: IRequestConfig<DownloadInLotRequestProps> = {
      url: `${this.base_url}/express-download/bulk`,
      method: MethodsTypes.post,
      data: {
        customerCareUserId,
        orderIds,
        orderAttachmentType,
        downloadType
      },
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.access_token()}`,
      },

    };

    axios.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        return error.response;
      }
    );

    const request = await axios(config)
      .then((result) => result);

    if (request.status !== 200) {
      toast.error('Desculpe, erro inesperado!');
      return false;
    }

    return request;
  }

  public async DownloadLegalDocument(
    customerCareUserId: string | undefined,
    attachmentFilter: string,
    legalDocumentId: string,
    setLoading: Dispatch<SetStateAction<boolean>>
  ) {
    const config: IRequestConfig<DownloadInLotRequestProps> = {
      url: `${this.base_url}/express-download`,
      method: MethodsTypes.post,
      data: {
        identifier: customerCareUserId,
        attachmentType: 'LEGAL_DOCUMENTS',
        attachmentFilter,
        legalDocumentId,
        communicationChannel: "PORTAL",
        forceDownload: true
      },
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.access_token()}`,
      },

    };

    axios.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        return error.response;
      }
    );

    const request = await axios(config)
      .then((result) => result);

    setLoading(false);

    if (request.status !== 200) {
      toast.error('Desculpe, erro inesperado!');
      return false;
    }

    const uriDocument = request.data.data.data[0].uri;
    if (!uriDocument || uriDocument === 'http://#') {
      toast.error('Documento não disponível para download');
      return false;
    }

    DownloadService.CreateDownloadWithBlank(uriDocument);
  }

  public async DownloadRegulatoryDocument(
    customerCareUserId: string | undefined,
    attachmentFilter: string,
    regulatoryDocumentId: string,
    enterpriseId: string,
    topicsSubsidiaryId: string) {
    const config: IRequestConfig<DownloadInLotRequestProps> = {
      url: `${this.base_url}/express-download`,
      method: MethodsTypes.post,
      data: {
        identifier: customerCareUserId,
        attachmentType: 'REGULATORY_DOCUMENTS',
        attachmentFilter,
        communicationChannel: "PORTAL",
        forceDownload: true,
        regulatoryDocumentId,
        enterpriseId,
        topicsSubsidiaryId
      },
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.access_token()}`,
      },

    };

    axios.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        return error.response;
      }
    );

    const request = await axios(config)
      .then((result) => result);

    if (request.status !== 200) {
      toast.error('Desculpe, erro inesperado!');
      return false;
    }

    return true;
  }

  public static CreateDownloadLink = (path: string | undefined) => {
    if (path) {
      const link = document.createElement('a');
      link.href = path;
      link.click();
    }
  };

  public static CreateDownloadWithBlank = (path: string | undefined) => {
    if (path) {
      const link = document.createElement('a');
      link.href = path;
      link.target = "_blank";
      link.click();
    }
  };

}
